import moment from 'moment';
import { getNullableMoment } from '@intelligence/v3/utils/date';
import {
  GAME_DATA_GENE_CONFIG_MAP,
  GAME_DATA_GENE_GROUPS,
  ENTITY_TYPE_CLASSNAME_MAP,
  getYearConfig,
} from '../constants';

export const getGameEntityTypeClassName = (entityType?: IEntityType): string => {
  if (!entityType) {
    return '';
  }
  return ENTITY_TYPE_CLASSNAME_MAP[entityType];
};

export const getQuarterReportDate = (date?: string): string => {
  const dateM = getNullableMoment(date) || moment().subtract(1, 'quarters');
  return dateM.startOf('quarter').format('YYYY-MM-DD');
};

/**
 * 从日期字符串中提取年份
 * @param date 日期字符串，如 '2025-07-01'
 * @returns 年份数字，如 2025；如果无法解析，默认返回当前年份
 */
export const getYearFromDate = (date?: string): number => {
  if (!date) {
    return moment().year();
  }
  const m = getNullableMoment(date);
  return m ? m.year() : moment().year();
};

/**
 * 获取角色配置，根据年份从对应的年份配置中获取
 * @param roleCode 角色code，如 'ISTJ'
 * @param year 年份数字，如 2025、2026；不传则使用当前年份
 */
export const getRoleConfig = (roleCode?: string, year?: number): IQuarterReportUserRoleConfig | null => {
  if (!roleCode) {
    return null;
  }

  const resolvedYear = year ?? moment().year();
  const yearCfg = getYearConfig(resolvedYear);
  const stdRoleCode = roleCode.toUpperCase();
  return yearCfg.USER_ROLE_CONFIG_MAP[stdRoleCode as unknown as IQuarterReportUserRoleCode] || null;
};

export const getGameDataGenes = (report?: IQuarterReportDetails | null): IQuarterReportGameDataGeneData[] => {
  const { tag1, tag2, tag3, tag4 } = report || {};
  const firstTag1 = tag1?.[0];
  const firstTag2 = tag2?.[0];
  const firstTag3 = tag3?.[0];
  const firstTag4 = tag4?.[0];

  return [
    {
      ...GAME_DATA_GENE_CONFIG_MAP[firstTag1?.ind as unknown as IQuarterReportGameDataGene],
      score: firstTag1?.score ?? null,
    },
    {
      ...GAME_DATA_GENE_CONFIG_MAP[firstTag2?.ind as unknown as IQuarterReportGameDataGene],
      score: firstTag2?.score ?? null,
    },
    {
      ...GAME_DATA_GENE_CONFIG_MAP[firstTag3?.ind as unknown as IQuarterReportGameDataGene],
      score: firstTag3?.score ?? null,
    },
    {
      ...GAME_DATA_GENE_CONFIG_MAP[firstTag4?.ind as unknown as IQuarterReportGameDataGene],
      score: firstTag4?.score ?? null,
    },
  ];
};

/**
 * 构建所有MBTI标签数据映射
 * 每组冲突标签分别对应接口返回的tag1、tag2、tag3、tag4，其中接口返回的为这一组冲突标签中分数高的标签
 * 每组冲突标签分数高的标签的score需要重新计算下，计算公式为score = Math.max(100 - score)，这样会显得标签的分数会更高些
 * 每组冲突标签中分数较低的标签的分数暂时处理为0
 * @param report 季报详情数据
 */
export const getGameDataAllGenreMap = (
  report?: IQuarterReportDetails | null,
): IRecord<IQuarterReportGameDataGeneData> => {
  const { tag1, tag2, tag3, tag4 } = report || {};
  const firstTag1 = tag1?.[0];
  const firstTag2 = tag2?.[0];
  const firstTag3 = tag3?.[0];
  const firstTag4 = tag4?.[0];
  const firstTags = [firstTag1, firstTag2, firstTag3, firstTag4];

  return GAME_DATA_GENE_GROUPS.reduce((map, reportGeneCodeGroups, index) => {
    const [leftReportGeneCode, rightReportGeneCode] = reportGeneCodeGroups || [];
    const leftReportGeneData = GAME_DATA_GENE_CONFIG_MAP[leftReportGeneCode];
    const rightReportGeneData = GAME_DATA_GENE_CONFIG_MAP[rightReportGeneCode];
    const firstTag = firstTags[index];
    const firstTagInd = firstTag?.ind;
    const firstTagScore = firstTag?.score ?? Number.NaN;
    const firstTagScoreMax = Math.max(firstTagScore, 100 - firstTagScore);
    return {
      ...map,
      [leftReportGeneCode]: {
        ...leftReportGeneData,
        score: firstTagInd === leftReportGeneCode ? firstTagScoreMax : 0,
      },
      [rightReportGeneCode]: {
        ...rightReportGeneData,
        score: firstTagInd === rightReportGeneCode ? firstTagScoreMax : 0,
      },
    };
  }, {});
};
